import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  isAxiosError,
} from 'axios';

import { useMutation } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import SearchRoundedIcon
  from '@mui/icons-material/SearchRounded';

import {
  askMarginCenterCommercialQuery,
} from '../services/marginCenterService';

import type {
  MarginCenterCommercialQueryContext,
  MarginCenterCommercialQueryField,
  MarginCenterCommercialQueryRow,
} from '../types/marginCenter';


interface CommercialQueryPanelProps {
  context:
    MarginCenterCommercialQueryContext | null;

  currencyCode: string | null;

  hasPartialBucket: boolean;

  scopeLabel?: string | null;
  questionTitle?: string;

  suggestedQuestions?: string[];

  onOpenCustomer: (
    customerId: string,
  ) => void;

  onOpenRelationship: (
    customerId: string,
    productId: string,
  ) => void;
}


const FIELD_LABELS:
Record<
  MarginCenterCommercialQueryField,
  string
> = {
  'entity.key':
    'Clave',

  'entity.label':
    'Entidad',

  'entity.customer_id':
    'Cliente ID',

  'entity.customer_name':
    'Cliente',

  'entity.product_id':
    'Producto ID',

  'entity.product_name':
    'Producto',

  'current.transaction_count':
    'Operaciones',

  'current.units':
    'Unidades',

  'current.revenue':
    'Ingresos',

  'current.cost_amount':
    'Coste',

  'current.margin_amount':
    'Margen',

  'current.margin_percentage':
    'Margen %',

  'current.average_net_unit_price':
    'Precio neto medio',

  'current.average_unit_cost':
    'Coste unitario medio',

  'current.cost_coverage_pct':
    'Cobertura coste',

  'current.resolved_revenue_coverage_pct':
    'Cobertura ingresos',

  'comparison.transaction_count':
    'Operaciones comp.',

  'comparison.units':
    'Unidades comp.',

  'comparison.revenue':
    'Ingresos comp.',

  'comparison.cost_amount':
    'Coste comp.',

  'comparison.margin_amount':
    'Margen comp.',

  'comparison.margin_percentage':
    'Margen % comp.',

  'comparison.average_net_unit_price':
    'Precio comp.',

  'comparison.average_unit_cost':
    'Coste unit. comp.',

  'comparison.cost_coverage_pct':
    'Cobertura coste comp.',

  'comparison.resolved_revenue_coverage_pct':
    'Cobertura ingresos comp.',

  'change.transaction_count':
    'Δ operaciones',

  'change.transaction_count_pct':
    'Δ operaciones %',

  'change.units':
    'Δ unidades',

  'change.units_pct':
    'Δ unidades %',

  'change.revenue':
    'Δ ingresos',

  'change.revenue_pct':
    'Δ ingresos %',

  'change.margin_amount':
    'Δ margen',

  'change.margin_amount_pct':
    'Δ margen %',

  'change.margin_percentage_points':
    'Δ margen % (pp)',

  'change.average_net_unit_price':
    'Δ precio',

  'change.average_net_unit_price_pct':
    'Δ precio %',

  'change.average_unit_cost':
    'Δ coste unit.',

  'change.average_unit_cost_pct':
    'Δ coste unit. %',
};


const getFieldValue = (
  row: MarginCenterCommercialQueryRow,
  field: MarginCenterCommercialQueryField,
): unknown => {
  const [
    scope,
    key,
  ] = field.split('.');

  if (!scope || !key) {
    return null;
  }

  const source =
    scope === 'entity'
      ? row.entity
      : scope === 'current'
        ? row.current
        : scope === 'comparison'
          ? row.comparison
          : scope === 'change'
            ? row.change
            : null;

  if (!source) {
    return null;
  }

  return (
    source as unknown as Record<string, unknown>
  )[key] ?? null;
};


const formatNumber = (
  value: number,
  maximumFractionDigits = 2,
): string => (
  new Intl.NumberFormat(
    'es-ES',
    {
      maximumFractionDigits,
    },
  ).format(value)
);


const formatCurrency = (
  value: number,
  currencyCode: string | null,
): string => {
  if (!currencyCode) {
    return formatNumber(
      value,
      2,
    );
  }

  try {
    return new Intl.NumberFormat(
      'es-ES',
      {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);

  } catch {
    return formatNumber(
      value,
      2,
    );
  }
};


const formatSignedNumber = (
  value: number,
  maximumFractionDigits = 2,
): string => {
  const formatted = formatNumber(
    Math.abs(value),
    maximumFractionDigits,
  );

  if (value > 0) {
    return `+${formatted}`;
  }

  if (value < 0) {
    return `-${formatted}`;
  }

  return formatted;
};


const formatSignedPercentage = (
  value: number,
): string => {
  const absoluteValue =
    Math.abs(value);

  const maximumFractionDigits =
    absoluteValue > 0
    && absoluteValue < 0.1
      ? 3
      : 1;

  return `${
    formatSignedNumber(
      value,
      maximumFractionDigits,
    )
  } %`;
};


const formatFieldValue = (
  field: MarginCenterCommercialQueryField,
  value: unknown,
  currencyCode: string | null,
): string => {
  if (
    value === null
    || value === undefined
  ) {
    return 'No calculable';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'number') {
    return String(value);
  }

  if (
    field ===
      'change.margin_percentage_points'
  ) {
    return `${
      formatSignedNumber(
        value,
        2,
      )
    } pp`;
  }

  if (
    field.endsWith('_pct')
    || field.endsWith(
      '.margin_percentage',
    )
    || field.endsWith(
      '.cost_coverage_pct',
    )
    || field.endsWith(
      '.resolved_revenue_coverage_pct',
    )
  ) {
    if (field.startsWith('change.')) {
      return formatSignedPercentage(
        value,
      );
    }

    return `${
      formatNumber(
        value,
        2,
      )
    } %`;
  }

  const isCurrency =
    [
      '.revenue',
      '.cost_amount',
      '.margin_amount',
      '.average_net_unit_price',
      '.average_unit_cost',
    ].some(
      (suffix) =>
        field.endsWith(suffix),
    );

  if (isCurrency) {
    if (field.startsWith('change.')) {
      const formatted =
        formatCurrency(
          Math.abs(value),
          currencyCode,
        );

      if (value > 0) {
        return `+${formatted}`;
      }

      if (value < 0) {
        return `-${formatted}`;
      }

      return formatted;
    }

    return formatCurrency(
      value,
      currencyCode,
    );
  }

  if (
    field.endsWith(
      '.transaction_count',
    )
  ) {
    return field.startsWith('change.')
      ? formatSignedNumber(
        value,
        0,
      )
      : formatNumber(
        value,
        0,
      );
  }

  if (field.startsWith('change.')) {
    return formatSignedNumber(
      value,
      2,
    );
  }

  return formatNumber(
    value,
    2,
  );
};


const CommercialQueryPanel:
React.FC<CommercialQueryPanelProps> = ({
  context,
  currencyCode,
  hasPartialBucket,
  scopeLabel = null,
  questionTitle,
  suggestedQuestions = [],
  onOpenCustomer,
  onOpenRelationship,
}) => {
  const [
    question,
    setQuestion,
  ] = useState('');

  const contextSignature =
    useMemo(
      () => JSON.stringify(context),
      [context],
    );

  const queryMutation = useMutation({
    mutationFn:
      askMarginCenterCommercialQuery,
  });

  const resetQuery =
    queryMutation.reset;

  useEffect(() => {
    resetQuery();
  }, [
    contextSignature,
    resetQuery,
  ]);

  const submitQuestion = () => {
    const normalizedQuestion =
      question.trim();

    if (
      !normalizedQuestion
      || !context
      || queryMutation.isPending
    ) {
      return;
    }

    queryMutation.mutate({
      question:
        normalizedQuestion,

      context,
    });
  };

  const response =
    queryMutation.data;

  const queryErrorDetail =
    useMemo(() => {
      const error =
        queryMutation.error;

      if (!error) {
        return null;
      }

      if (isAxiosError(error)) {
        const status =
          error.response?.status;

        const data =
          error.response?.data;

        const detail =
          data
          && typeof data === 'object'
          && 'detail' in data
            ? String(data.detail)
            : error.message;

        return status
          ? `HTTP ${status} · ${detail}`
          : detail;
      }

      return error instanceof Error
        ? error.message
        : 'Error desconocido.';
    }, [
      queryMutation.error,
    ]);

  const result =
    response?.status === 'ok'
      ? response.result
      : null;

  const fields =
    result?.normalized_query
      .display_fields
    ?? [];

  const canOpenRow = (
    row: MarginCenterCommercialQueryRow,
  ): boolean => (
    (
      row.entity.dimension === 'customer'
      && Boolean(
        row.entity.customer_id,
      )
    )
    || (
      row.entity.dimension
        === 'customer_product'
      && Boolean(
        row.entity.customer_id,
      )
      && Boolean(
        row.entity.product_id,
      )
    )
  );

  const openRow = (
    row: MarginCenterCommercialQueryRow,
  ) => {
    if (
      row.entity.dimension === 'customer'
      && row.entity.customer_id
    ) {
      onOpenCustomer(
        row.entity.customer_id,
      );

      return;
    }

    if (
      row.entity.dimension
        === 'customer_product'
      && row.entity.customer_id
      && row.entity.product_id
    ) {
      onOpenRelationship(
        row.entity.customer_id,
        row.entity.product_id,
      );
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        p: 1.75,
        borderRadius: 2.5,
        border:
          '1px solid rgba(129,140,248,0.14)',
        background:
          'linear-gradient(180deg, rgba(18,27,47,0.68) 0%, rgba(10,18,33,0.84) 100%)',
        boxShadow:
          '0 14px 34px rgba(2,6,23,0.16)',
        minWidth: 0,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: '#38bdf8',
          fontWeight: 850,
          letterSpacing: 0.9,
          lineHeight: 1.2,
        }}
      >
        Consulta comercial
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          color: '#f8fafc',
          fontWeight: 850,
          mt: 0.2,
        }}
      >
        {questionTitle ?? (scopeLabel
          ? 'Pregunta sobre este cliente'
          : '¿Qué quieres consultar?')}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
          mt: 0.2,
          lineHeight: 1.45,
        }}
      >
        {scopeLabel
          ? `Contexto fijo · ${scopeLabel}. La respuesta usa el periodo y la comparación seleccionados.`
          : (
            'Escribe una pregunta sobre clientes, '
            + 'productos o relaciones. El resultado '
            + 'se obtiene sobre el periodo seleccionado.'
          )}
      </Typography>

      {suggestedQuestions.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          {suggestedQuestions.map(
            (suggestion) => (
              <Button
                key={suggestion}
                size="small"
                variant="text"
                onClick={() =>
                  setQuestion(suggestion)
                }
                sx={{
                  px: 0.75,
                  py: 0.25,
                  minWidth: 0,
                  color: '#94a3b8',
                  fontSize: '0.7rem',
                  textTransform: 'none',
                }}
              >
                {suggestion}
              </Button>
            ),
          )}
        </Stack>
      )}

      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={1}
        alignItems="stretch"
        sx={{ mt: 1.25 }}
      >
        <TextField
          fullWidth
          size="small"
          value={question}
          disabled={!context}
          placeholder={
            'Ej. clientes con ingresos -20 % y margen % al alza'
          }
          onChange={(event) => {
            setQuestion(
              event.target.value,
            );
          }}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter'
              && !event.shiftKey
            ) {
              event.preventDefault();
              submitQuestion();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor:
                'rgba(2,6,23,0.28)',
            },
          }}
        />

        <Button
          variant="outlined"
          startIcon={
            <SearchRoundedIcon />
          }
          disabled={
            !context
            || !question.trim()
            || queryMutation.isPending
          }
          onClick={submitQuestion}
          sx={{
            minWidth: 118,
            textTransform: 'none',
            fontWeight: 850,
            whiteSpace: 'nowrap',
          }}
        >
          Consultar
        </Button>
      </Stack>

      {queryMutation.isPending && (
        <LinearProgress
          sx={{ mt: 1.2 }}
        />
      )}

      {!context && (
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            mt: 1.25,
          }}
        >
          No hay un periodo analítico disponible
          para ejecutar consultas.
        </Typography>
      )}

      {queryMutation.isError && (
        <Alert
          severity="warning"
          sx={{ mt: 1.25 }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 800 }}
          >
            No se ha podido interpretar o ejecutar
            la consulta.
          </Typography>

          {queryErrorDetail && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.4,
              }}
            >
              {queryErrorDetail}
            </Typography>
          )}
        </Alert>
      )}

      {response?.status
        === 'needs_clarification' && (
        <Alert
          severity="info"
          sx={{ mt: 1.25 }}
        >
          {response.message}
        </Alert>
      )}

      {response?.status
        === 'unsupported' && (
        <Alert
          severity="info"
          sx={{ mt: 1.25 }}
        >
          {response.message}
        </Alert>
      )}

      {result?.status
        === 'no_results' && (
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            mt: 1.25,
          }}
        >
          No hay resultados que cumplan la
          consulta en la selección actual.
        </Typography>
      )}

      {result?.status
        === 'empty_dataset' && (
        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            mt: 1.25,
          }}
        >
          No hay datos transaccionales
          disponibles.
        </Typography>
      )}

      {result?.status === 'ok' && (
        <Box sx={{ mt: 1.25 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#f8fafc',
              fontWeight: 850,
              mb: 0.75,
            }}
          >
            {result.page.returned_count}
            {' '}
            resultados
            {result.page.has_more
              ? ' en esta página'
              : ''}
          </Typography>

          <TableContainer
            sx={{
              // PRC-032: sin barra vertical propia dentro de la página.
              borderRadius: 1.75,
              border:
                '1px solid rgba(148,163,184,0.12)',
            }}
          >
            <Table
              stickyHeader
              size="small"
            >
              <TableHead>
                <TableRow>
                  {fields.map(
                    (field) => (
                      <TableCell
                        key={field}
                        sx={{
                          bgcolor:
                            'rgba(15,23,42,0.98)',
                          color: '#94a3b8',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          whiteSpace: 'nowrap',
                          borderBottom:
                            '1px solid rgba(148,163,184,0.12)',
                        }}
                      >
                        {FIELD_LABELS[field]}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {result.rows.map(
                  (row) => {
                    const navigable =
                      canOpenRow(row);

                    return (
                      <TableRow
                        hover={navigable}
                        key={row.entity.key}
                        onClick={() => {
                          if (navigable) {
                            openRow(row);
                          }
                        }}
                        sx={{
                          cursor:
                            navigable
                              ? 'pointer'
                              : 'default',

                          '& td': {
                            borderBottom:
                              '1px solid rgba(148,163,184,0.08)',
                          },
                        }}
                      >
                        {fields.map(
                          (field) => (
                            <TableCell
                              key={field}
                              sx={{
                                color: '#e2e8f0',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatFieldValue(
                                field,
                                getFieldValue(
                                  row,
                                  field,
                                ),
                                currencyCode,
                              )}
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {result.page.has_more && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#64748b',
                mt: 0.65,
              }}
            >
              Hay más resultados disponibles.
              La paginación de Consulta Comercial
              se conectará en la siguiente
              iteración.
            </Typography>
          )}
        </Box>
      )}

      {hasPartialBucket && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#64748b',
            mt: 1.2,
            pt: 1,
            borderTop:
              '1px solid rgba(148,163,184,0.10)',
            lineHeight: 1.45,
          }}
        >
          La selección contiene al menos un
          bucket temporal parcial.
        </Typography>
      )}
    </Paper>
  );
};


export default CommercialQueryPanel;
